import React, { useState } from 'react';
import { SearchIcon, PlusIcon, XIcon, CreditCardIcon, CheckIcon } from 'lucide-react';
interface BankAccount {
  id: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  currency: string;
  isPrimary: boolean;
  associations: Array<{
    type: string;
    name: string;
    id: string;
  }>;
}
interface BankAccountSelectorProps {
  selectedBankAccountIds: string[];
  onBankAccountsChange: (ids: string[]) => void;
  availableBankAccounts: BankAccount[];
  onAddBankAccount: (account: Omit<BankAccount, 'id' | 'associations'>) => void;
}
export function BankAccountSelector({
  selectedBankAccountIds,
  onBankAccountsChange,
  availableBankAccounts,
  onAddBankAccount
}: BankAccountSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  // New account form state
  const [newAccount, setNewAccount] = useState({
    bankName: '',
    accountName: '',
    accountNumber: '',
    currency: 'USD',
    isPrimary: false
  });
  const selectedAccounts = availableBankAccounts.filter(acc => selectedBankAccountIds.includes(acc.id));
  const filteredAccounts = availableBankAccounts.filter(acc => {
    const matchesSearch = acc.bankName.toLowerCase().includes(searchTerm.toLowerCase()) || acc.accountName.toLowerCase().includes(searchTerm.toLowerCase()) || acc.accountNumber.includes(searchTerm);
    const notAlreadySelected = !selectedBankAccountIds.includes(acc.id);
    return matchesSearch && notAlreadySelected;
  });
  const handleAddClick = (accountId: string) => {
    onBankAccountsChange([...selectedBankAccountIds, accountId]);
    setSearchTerm('');
    setShowDropdown(false);
  };
  const handleRemoveClick = (accountId: string) => {
    onBankAccountsChange(selectedBankAccountIds.filter(id => id !== accountId));
  };
  const handleCreateAccount = () => {
    if (!newAccount.bankName || !newAccount.accountNumber) return;
    onAddBankAccount(newAccount);
    setNewAccount({
      bankName: '',
      accountName: '',
      accountNumber: '',
      currency: 'USD',
      isPrimary: false
    });
    setShowAddForm(false);
  };
  return <div className="space-y-4" data-id="element-8344">
      {/* Header with Add Button */}
      <div className="flex justify-between items-start" data-id="element-8345">
        <div data-id="element-8346">
          <h4 className="text-sm font-medium text-gray-700" data-id="element-8347">
            {selectedAccounts.length > 0 ? 'Associated Bank Accounts' : 'Add Bank Account'}
          </h4>
          {selectedAccounts.length === 0 && <p className="text-sm text-gray-500 mt-1" data-id="element-8348">
              No bank accounts associated yet
            </p>}
        </div>
        {!showAddForm && <button type="button" onClick={() => setShowAddForm(true)} className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8349">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-8350" />
            New
          </button>}
      </div>

      {/* Selected Accounts List */}
      {selectedAccounts.length > 0 && <div className="space-y-3" data-id="element-8351">
          {selectedAccounts.map(account => <div key={account.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-8352">
              <div className="flex items-start space-x-3" data-id="element-8353">
                <div className="flex-shrink-0" data-id="element-8354">
                  <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-8355">
                    <CreditCardIcon className="h-5 w-5 text-indigo-600" data-id="element-8356" />
                  </div>
                </div>
                <div className="flex-1 min-w-0" data-id="element-8357">
                  <div className="flex items-center" data-id="element-8358">
                    <p className="text-sm font-medium text-gray-900" data-id="element-8359">
                      {account.bankName}
                    </p>
                    {account.isPrimary && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-8360">
                        Primary
                      </span>}
                  </div>
                  <p className="text-sm text-gray-500" data-id="element-8361">{account.accountName}</p>
                  <p className="text-xs text-gray-400 font-mono mt-1" data-id="element-8362">
                    •••• {account.accountNumber.slice(-4)} • {account.currency}
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => handleRemoveClick(account.id)} className="ml-4 text-gray-400 hover:text-red-600" data-id="element-8363">
                <XIcon className="h-5 w-5" data-id="element-8364" />
              </button>
            </div>)}
        </div>}

      {/* Add Form */}
      {showAddForm && <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-8365">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-8366">
            New Bank Account
          </h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-8367">
            <div data-id="element-8368">
              <label className="block text-xs font-medium text-gray-700" data-id="element-8369">
                Bank Name
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={newAccount.bankName} onChange={e => setNewAccount({
            ...newAccount,
            bankName: e.target.value
          })} data-id="element-8370" />
            </div>
            <div data-id="element-8371">
              <label className="block text-xs font-medium text-gray-700" data-id="element-8372">
                Account Name
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={newAccount.accountName} onChange={e => setNewAccount({
            ...newAccount,
            accountName: e.target.value
          })} data-id="element-8373" />
            </div>
            <div data-id="element-8374">
              <label className="block text-xs font-medium text-gray-700" data-id="element-8375">
                Account Number
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={newAccount.accountNumber} onChange={e => setNewAccount({
            ...newAccount,
            accountNumber: e.target.value
          })} data-id="element-8376" />
            </div>
            <div data-id="element-8377">
              <label className="block text-xs font-medium text-gray-700" data-id="element-8378">
                Currency
              </label>
              <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={newAccount.currency} onChange={e => setNewAccount({
            ...newAccount,
            currency: e.target.value
          })} data-id="element-8379">
                <option value="USD" data-id="element-8380">USD</option>
                <option value="EUR" data-id="element-8381">EUR</option>
                <option value="GBP" data-id="element-8382">GBP</option>
                <option value="AUD" data-id="element-8383">AUD</option>
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-8384">
              <div className="flex items-center" data-id="element-8385">
                <input id="isPrimary" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" checked={newAccount.isPrimary} onChange={e => setNewAccount({
              ...newAccount,
              isPrimary: e.target.checked
            })} data-id="element-8386" />
                <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-900" data-id="element-8387">
                  Set as primary account
                </label>
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2" data-id="element-8388">
            <button type="button" onClick={() => setShowAddForm(false)} className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-8389">
              Cancel
            </button>
            <button type="button" onClick={handleCreateAccount} className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-8390">
              Add Account
            </button>
          </div>
        </div>}
    </div>;
}